/**
 * Certification Data Sharing — V96-5-4..5-6 / ADR-0045 §6.
 *
 * The first agency-facing surface. Security model:
 *   - Grants are created/revoked by workspace owner/admin (route layer).
 *     A grant binds ONE workspace + ONE active FedRAMP certification profile
 *     + an allow-list of package kinds + an expiry (≤ 1 year).
 *   - Agencies authenticate with a bearer token issued per grant. Only the
 *     token's SHA-256 is stored (plus a 4-char hint for display); the
 *     plaintext is returned exactly once at issue time.
 *   - Resolving a token re-checks grant status, expiry and profile status
 *     on every request; anything off → null (caller answers 401/404 without
 *     saying which).
 *   - The workspace/profile an agency sees comes from the grant, never from
 *     a request parameter; package reads are filtered by grant workspace,
 *     profile and allowed kinds.
 *   - Every read is logged (grant, token, action, package, salted IP hash);
 *     log retention is 6 months (CDS-CSO rules); `purgeAccessLog` enforces it.
 */
import { programHas } from '../programs/manifests.js';
export const CDS_MAX_GRANT_DAYS = 366;
export const CDS_LOG_RETENTION_DAYS = 183;
export const CDS_PACKAGE_KINDS = ['certification_package_overview', 'security_decision_record', 'ongoing_certification_report'];
/**
 * V111-5-3 (ADR-0094 §5): insurer-facing kinds, served as PROJECTIONS of the
 * latest SIGNED insurability pack (migration 724) — everything an insurer
 * token reads is a subset of signed content and verifies at
 * /verify/insurability-pack/:id. (`ai_disclosure` here is the insurer view of
 * recognized risks + exposures; the ADR-0023 supplier disclosure is separate.)
 * An insurer grant binds the WORKSPACE (profile_id NULL, migration 727); the
 * two kind families never mix on one grant.
 */
export const CDS_INSURER_KINDS = ['ai_inventory', 'ai_disclosure', 'loss_history'];
/**
 * V113-5-2 (ADR-0103 §4; D-113-8): the continuous-monitoring feed is its OWN kind family — never on a grant
 * with agency kinds or the v11.1 insurer views. A monitoring grant binds the workspace (profile NULL), needs a
 * signed insurability pack, and names its recipient type (`insurer` or `customer` — enterprise TPRM, D-113-24;
 * migration 827's CHECK). Kind family and signed fields are identical for both.
 */
export const CDS_MONITORING_KINDS = ['continuous_monitoring'];
export const CDS_RECIPIENT_TYPES = ['insurer', 'customer'];
export const CDS_ALL_PACKAGE_KINDS = [...CDS_PACKAGE_KINDS, ...CDS_INSURER_KINDS, ...CDS_MONITORING_KINDS];
/** The kind family of a kind (agency / insurer views / continuous monitoring); null for an unknown kind. */
export function cdsKindFamily(kind) {
    if (CDS_PACKAGE_KINDS.includes(kind))
        return 'agency';
    if (CDS_INSURER_KINDS.includes(kind))
        return 'insurer';
    if (CDS_MONITORING_KINDS.includes(kind))
        return 'monitoring';
    return null;
}
export class CdsValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'CdsValidationError';
    }
}
const enc = new TextEncoder();
async function sha256Hex(s) {
    const d = new Uint8Array(await crypto.subtle.digest('SHA-256', enc.encode(s)));
    return Array.from(d).map((b) => b.toString(16).padStart(2, '0')).join('');
}
function b64url(bytes) {
    let s = '';
    for (const b of bytes)
        s += String.fromCharCode(b);
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
export async function createGrant(db, input) {
    const kinds = input.package_kinds ?? ['certification_package_overview', 'ongoing_certification_report'];
    if (!kinds.length || kinds.some((k) => !CDS_ALL_PACKAGE_KINDS.includes(k))) {
        throw new CdsValidationError(`package_kinds must be a non-empty subset of ${CDS_ALL_PACKAGE_KINDS.join(', ')}.`);
    }
    const families = new Set(kinds.map(cdsKindFamily));
    if (families.size > 1) {
        throw new CdsValidationError('Kind families never mix on one grant (agency, insurer views, continuous monitoring); create one grant per audience.');
    }
    const hasInsurer = families.has('insurer');
    const hasMonitoring = families.has('monitoring');
    if (!hasMonitoring && input.recipient_type)
        throw new CdsValidationError('recipient_type applies to continuous_monitoring grants only.');
    if (hasMonitoring && !input.recipient_type)
        throw new CdsValidationError('A continuous_monitoring grant names its recipient_type (insurer or customer).');
    const recipientType = hasMonitoring ? input.recipient_type : null;
    if (recipientType && !CDS_RECIPIENT_TYPES.includes(recipientType)) {
        throw new CdsValidationError(`recipient_type must be one of ${CDS_RECIPIENT_TYPES.join(', ')}.`);
    }
    if (hasInsurer || hasMonitoring) {
        // V111-5-3: an insurer grant binds the workspace; its reads come from the
        // latest SIGNED insurability pack, so one must exist before sharing.
        if (input.profile_id)
            throw new CdsValidationError('An insurer-kind grant takes no certification profile.');
        const pack = await db.query(`SELECT package_id FROM insurability_packs WHERE workspace_id = ? LIMIT 1`, [input.workspace_id]);
        if (!pack.results[0]) {
            throw new CdsValidationError('Sign an insurability pack before sharing it with an insurer.');
        }
    }
    else {
        if (!input.profile_id)
            throw new CdsValidationError('profile_id must be an active FedRAMP certification profile in this workspace.');
        const profile = await db.query(`SELECT program, status FROM certification_profiles WHERE profile_id = ? AND workspace_id = ?`, [input.profile_id, input.workspace_id]);
        const p = profile.results[0];
        if (!p || !programHas(p.program, (m) => m.features.cds) || p.status !== 'active') {
            throw new CdsValidationError('profile_id must be an active FedRAMP certification profile in this workspace.');
        }
    }
    const name = input.agency_name.trim();
    if (name.length < 2 || name.length > 200)
        throw new CdsValidationError('agency_name must be 2–200 characters.');
    if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(input.agency_contact_email) || input.agency_contact_email.length > 254) {
        throw new CdsValidationError('agency_contact_email must be an email address.');
    }
    const expires = Date.parse(input.expires_at);
    if (!Number.isFinite(expires) || expires <= input.now.getTime() || expires > input.now.getTime() + CDS_MAX_GRANT_DAYS * 86_400_000) {
        throw new CdsValidationError(`expires_at must be in the future and within ${CDS_MAX_GRANT_DAYS} days.`);
    }
    await db.execute(recipientType
        ? `INSERT INTO cds_grants (grant_id, workspace_id, profile_id, agency_name, agency_contact_email, package_kinds_json, expires_at, status, created_by_user_id, created_at, recipient_type)
         VALUES (?, ?, ?, ?, ?, ?, ?, 'active', ?, ?, ?)`
        : `INSERT INTO cds_grants (grant_id, workspace_id, profile_id, agency_name, agency_contact_email, package_kinds_json, expires_at, status, created_by_user_id, created_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, 'active', ?, ?)`, [input.grant_id, input.workspace_id, input.profile_id, name, input.agency_contact_email.toLowerCase(),
        JSON.stringify([...new Set(kinds)]), new Date(expires).toISOString(), input.created_by_user_id, input.now.toISOString(),
        ...(recipientType ? [recipientType] : [])]);
    return (await getGrant(db, input.workspace_id, input.grant_id));
}
export async function getGrant(db, workspaceId, grantId) {
    const r = await db.query(`SELECT * FROM cds_grants WHERE grant_id = ? AND workspace_id = ?`, [grantId, workspaceId]);
    return r.results[0] ?? null;
}
/** Grants with an access inventory (last access, reads, active tokens). */
export async function listGrants(db, workspaceId) {
    const r = await db.query(`SELECT g.*,
            (SELECT MAX(accessed_at) FROM cds_access_log l WHERE l.grant_id = g.grant_id) AS last_access_at,
            (SELECT COUNT(*) FROM cds_access_log l WHERE l.grant_id = g.grant_id) AS reads,
            (SELECT COUNT(*) FROM cds_tokens t WHERE t.grant_id = g.grant_id AND t.revoked_at IS NULL) AS active_tokens
       FROM cds_grants g WHERE g.workspace_id = ? ORDER BY g.created_at DESC`, [workspaceId]);
    return r.results;
}
export async function revokeGrant(db, workspaceId, grantId, userId, now) {
    const r = await db.execute(`UPDATE cds_grants SET status = 'revoked', revoked_at = ?, revoked_by_user_id = ? WHERE grant_id = ? AND workspace_id = ? AND status = 'active'`, [now.toISOString(), userId, grantId, workspaceId]);
    if ((r.meta?.changes ?? 0) === 0)
        return false;
    await db.execute(`UPDATE cds_tokens SET revoked_at = ? WHERE grant_id = ? AND revoked_at IS NULL`, [now.toISOString(), grantId]);
    return true;
}
/** Issue a token for an active grant. The plaintext is returned once and never stored. */
export async function issueToken(db, workspaceId, grantId, tokenId, now) {
    const g = await getGrant(db, workspaceId, grantId);
    if (!g || g.status !== 'active' || Date.parse(g.expires_at) <= now.getTime())
        return null;
    const token = `cds_${b64url(crypto.getRandomValues(new Uint8Array(32)))}`;
    const hint = token.slice(-4);
    await db.execute(`INSERT INTO cds_tokens (token_id, grant_id, token_sha256, token_hint, created_at) VALUES (?, ?, ?, ?, ?)`, [tokenId, grantId, await sha256Hex(token), hint, now.toISOString()]);
    return { token_id: tokenId, token, token_hint: hint };
}
export async function revokeToken(db, workspaceId, grantId, tokenId, now) {
    const r = await db.execute(`UPDATE cds_tokens SET revoked_at = ?
      WHERE token_id = ? AND grant_id = ? AND revoked_at IS NULL
        AND grant_id IN (SELECT grant_id FROM cds_grants WHERE workspace_id = ?)`, [now.toISOString(), tokenId, grantId, workspaceId]);
    return (r.meta?.changes ?? 0) > 0;
}
/** Bearer token → grant. Null for anything not currently valid (caller must not say why). */
export async function resolveCdsToken(db, token, now) {
    if (!token || !/^cds_[A-Za-z0-9_-]{43}$/.test(token))
        return null;
    // Insurer grants carry no profile (727); a bound profile must still be active.
    const r = await db.query(`SELECT g.*, t.token_id
       FROM cds_tokens t JOIN cds_grants g ON g.grant_id = t.grant_id
       LEFT JOIN certification_profiles p ON p.profile_id = g.profile_id AND p.workspace_id = g.workspace_id
      WHERE t.token_sha256 = ? AND t.revoked_at IS NULL AND g.status = 'active'
        AND (g.profile_id IS NULL OR p.status = 'active')`, [await sha256Hex(token)]);
    const g = r.results[0];
    if (!g || Date.parse(g.expires_at) <= now.getTime())
        return null;
    await db.execute(`UPDATE cds_tokens SET last_used_at = ? WHERE token_id = ?`, [now.toISOString(), g.token_id]);
    return {
        grant_id: g.grant_id,
        token_id: g.token_id,
        workspace_id: g.workspace_id,
        profile_id: g.profile_id,
        agency_name: g.agency_name,
        package_kinds: JSON.parse(g.package_kinds_json),
    };
}
export async function logCdsAccess(db, access, entry) {
    const ipHash = entry.client_ip ? await sha256Hex(`${access.workspace_id}:${entry.client_ip}`) : null;
    await db.execute(`INSERT INTO cds_access_log (log_id, grant_id, workspace_id, token_id, action, package_id, client_ip_sha256, accessed_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?)`, [entry.log_id, access.grant_id, access.workspace_id, access.token_id, entry.action, entry.package_id ?? null, ipHash, entry.now.toISOString()]);
}
/**
 * V98-7-3 H2: CDS serves only packages released through the lifecycle
 * (`releaseSignedCertificationPackage`): a signed, approver-routed signature
 * request on a certification package whose downstream send points at this
 * package. A package issued any other way (the retired v9.6 one-step route)
 * is never served to an agency.
 */
const RELEASED_SQL = `EXISTS (SELECT 1 FROM signature_requests sr
   WHERE sr.workspace_id = certification_packages.workspace_id AND sr.artifact_kind = 'certification_package'
     AND sr.state = 'signed' AND sr.downstream_sent_at IS NOT NULL AND sr.downstream_ref = certification_packages.package_id)`;
/** Packages visible to a grant — its workspace, its profile, its kinds, released through the lifecycle only. */
export async function listSharedPackages(db, access) {
    // Insurer grants read the insurability pack (listSharedInsurerViews), never
    // certification packages; they carry no profile.
    if (!access.profile_id)
        return [];
    // d1-in-list: bounded — package_kinds is validated as a subset of CDS_PACKAGE_KINDS at grant creation
    const kinds = access.package_kinds;
    const r = await db.query(`SELECT package_id, kind, period_start, period_end, content_sha256, created_at
       FROM certification_packages
      WHERE workspace_id = ? AND profile_id = ? AND kind IN (${kinds.map(() => '?').join(',')}) AND ${RELEASED_SQL}
      ORDER BY created_at DESC LIMIT 200`, [access.workspace_id, access.profile_id, ...kinds]);
    return r.results;
}
export async function getSharedPackage(db, access, packageId) {
    if (!access.profile_id)
        return null;
    // d1-in-list: bounded — package_kinds is validated as a subset of CDS_PACKAGE_KINDS at grant creation
    const kinds = access.package_kinds;
    const r = await db.query(`SELECT package_id, kind, content_json, content_sha256, hmac_key_id, hmac_signature, created_at
       FROM certification_packages
      WHERE package_id = ? AND workspace_id = ? AND profile_id = ? AND kind IN (${kinds.map(() => '?').join(',')}) AND ${RELEASED_SQL}`, [packageId, access.workspace_id, access.profile_id, ...kinds]);
    return r.results[0] ?? null;
}
/**
 * V111-5-3: insurer views — projections of the LATEST SIGNED insurability
 * pack. Every byte served is a subset of signed content; the response carries
 * the pack's identity + digest so the insurer verifies at
 * /verify/insurability-pack/:package_id.
 */
export async function listSharedInsurerViews(db, access) {
    const kinds = access.package_kinds.filter((k) => CDS_INSURER_KINDS.includes(k));
    if (kinds.length === 0)
        return [];
    const pack = (await db.query(`SELECT package_id, version, content_sha256, signed_at FROM insurability_packs
        WHERE workspace_id = ? ORDER BY version DESC LIMIT 1`, [access.workspace_id])).results[0];
    if (!pack)
        return [];
    return kinds.map((kind) => ({ kind, ...pack }));
}
export async function getSharedInsurerView(db, access, kind) {
    if (!CDS_INSURER_KINDS.includes(kind) ||
        !access.package_kinds.includes(kind)) {
        return null;
    }
    const pack = (await db.query(`SELECT package_id, version, content_json, content_sha256, hmac_key_id, signed_at FROM insurability_packs
        WHERE workspace_id = ? ORDER BY version DESC LIMIT 1`, [access.workspace_id])).results[0];
    if (!pack)
        return null;
    const doc = JSON.parse(pack.content_json);
    const projection = kind === 'ai_inventory'
        ? { cover: doc.cover, systems: doc.systems }
        : kind === 'ai_disclosure'
            ? { cover: doc.cover, risks: doc.risks, uncovered_exposures: doc.uncovered_exposures }
            : { cover: doc.cover, loss_ledger: doc.loss_ledger };
    return {
        kind: kind,
        package_id: pack.package_id,
        version: pack.version,
        content_sha256: pack.content_sha256,
        hmac_key_id: pack.hmac_key_id,
        signed_at: pack.signed_at,
        content: projection,
    };
}
export async function listAccessLog(db, workspaceId, grantId, limit = 200) {
    const r = await db.query(`SELECT l.log_id, l.action, l.package_id, l.accessed_at, t.token_hint
       FROM cds_access_log l LEFT JOIN cds_tokens t ON t.token_id = l.token_id
      WHERE l.grant_id = ? AND l.workspace_id = ? ORDER BY l.accessed_at DESC LIMIT ?`, [grantId, workspaceId, Math.min(Math.max(limit, 1), 1000)]);
    return r.results;
}
/** Retention + expiry housekeeping (hourly sweep). */
export async function cdsHousekeeping(db, now) {
    const cutoff = new Date(now.getTime() - CDS_LOG_RETENTION_DAYS * 86_400_000).toISOString();
    const purged = await db.execute(`DELETE FROM cds_access_log WHERE accessed_at < ?`, [cutoff]);
    const expired = await db.execute(`UPDATE cds_grants SET status = 'expired' WHERE status = 'active' AND expires_at <= ?`, [now.toISOString()]);
    return { purged: purged.meta?.changes ?? 0, expired: expired.meta?.changes ?? 0 };
}
//# sourceMappingURL=cds-service.js.map